import { useMemo, useState, type CSSProperties } from 'react';
import { Box, Button, Flex, Grid, Modal, Radio, Select, Stack, Text } from '@mantine/core';
import classes from './ModalLabelGeneration.module.css';

export type Quadrant = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const QUADRANTS: Quadrant[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const labelFor = (q: Quadrant) =>
  ({
    'top-left': 'In alto a sinistra',
    'top-right': 'In alto a destra',
    'bottom-left': 'In basso a sinistra',
    'bottom-right': 'In basso a destra',
  } as const)[q];

const LABELS_OPTIONS = Array.from({ length: 10 }, (_, i) => String(i + 1));

export interface ModalLabelGenerationProps {
  opened: boolean;
  onClose: () => void;
  skus: string[];
}

const ModalLabelGeneration = ({ opened, onClose, skus }: ModalLabelGenerationProps) => {
  const [selected, setSelected] = useState<Quadrant>('top-left');
  const [labelsPerProduct, setLabelsPerProduct] = useState<string>('1');

  const selectedIndex = useMemo(() => QUADRANTS.indexOf(selected), [selected]);

  const handleConfirm = () => {
    const labels = parseInt(labelsPerProduct, 10) || 1;
    // For now, just print the selected SKUs and the user choices
    // Integrate with the actual printing/generation flow later
    // eslint-disable-next-line no-console
    console.log('[ModalLabelGeneration] Confirm:', {
      skus,
      quadrant: selected,
      labelsPerProduct: labels,
    });
    onClose();
  };

  // Helper to render each quadrant cell
  const renderCell = (index: number) => {
    const q = QUADRANTS[index];
    const isSelected = selectedIndex === index;
    return (
      <Box
        key={index}
        component="button"
        type="button"
        onClick={() => setSelected(q)}
        className={classes.cell}
      >
        <Box className={classes.cellInner} />
        <Box className={classes.indexBadge}>{index + 1}</Box>
        
        {/* Selected highlight overlay - matches template structure */}
        {isSelected && (
          <Box className={classes.cellHighlight}>
            <Box className={classes.highlightFill} />
            <Box className={classes.highlightRing} />
            <Box className={classes.highlightCheck}>✓</Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Modal.Root opened={opened} onClose={onClose} centered size="lg" radius="md">
      <Modal.Overlay />
      <Modal.Content aria-modal="true">
        <Box p="md">
          <Modal.Header>
            <Text fw={600}>Genera etichette</Text>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Grid gutter="lg">
              {/* Left: A4 preview */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="sm">
                  <Text fw={500}>Punto di partenza di stampa</Text>
                  <Text size="xs" c="dimmed">
                    Seleziona il quadrante da cui iniziare a stampare l'etichetta. L'anteprima si aggiorna in tempo reale.
                  </Text>
                  <Box className={classes.previewWrapper}>
                    {/* A4 aspect ratio via fixed dimensions - matches template structure */}
                    <Box className={classes.a4Frame}>
                      <Box className={classes.a4Grid}>
                        {[0, 1, 2, 3].map((i) => renderCell(i))}
                      </Box>
                      <Box className={classes.a4Badge}>A4 preview</Box>
                    </Box>
                    <Text className={classes.legend} ta="center" size="xs" c="dimmed" mt={6}>
                      1 = Alto sinistra • 2 = Alto destra • 3 = Basso sinistra • 4 = Basso destra
                    </Text>
                  </Box>
                </Stack>
              </Grid.Col>

              {/* Right: options */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="md">
                  <div>
                    <Text fw={500} mb={8}>Quadrante</Text>
                    <Radio.Group value={selected} onChange={(v) => setSelected(v as Quadrant)}>
                      <Stack gap={8}>
                        {QUADRANTS.map((q) => (
                          <Radio key={q} value={q} label={labelFor(q)} />
                        ))}
                      </Stack>
                    </Radio.Group>
                  </div>

                  <div>
                    <Text fw={500} mb={6}>Numero di etichette per articolo</Text>
                    <Select
                      data={LABELS_OPTIONS.map((n) => ({ value: n, label: n }))}
                      value={labelsPerProduct}
                      onChange={(val) => setLabelsPerProduct(val ?? '1')}
                      className={classes.select}
                      aria-label="Numero di etichette per articolo"
                    />
                    <Text size="xs" c="dimmed" mt={6}>
                      Ogni articolo selezionato stamperà questo numero di etichette.
                    </Text>
                  </div>

                  <Flex gap="sm" justify="flex-end" mt="sm">
                    <Button variant="outline" radius="lg" className={classes.btnSecondary} onClick={onClose}>
                      Annulla
                    </Button>
                    <Button radius="lg" className={classes.btnPrimary} onClick={handleConfirm}>
                      Conferma
                    </Button>
                  </Flex>
                  <Text size="xs" c="dimmed">Suggerimento: puoi anche cliccare direttamente sull'anteprima per selezionare un quadrante.</Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Modal.Body>
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};

export default ModalLabelGeneration;
